import { IconVariants, MultiSelect } from "@next-platform/client-common-spicy-ui";
import { useApp, useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useMemo, useState } from "react";

import { MAXIMUM_TAG_LIMIT } from "@/constants";
import useToast from "@/hooks/useToast";
import { cn } from "@/utils";

type TagsFieldProps = {
  initialValues: Tag[];
  disabled: boolean;
  setSelectedTags: (value: Tag[]) => void;
  placeholder?: string;
  error?: boolean;
  readOnly?: boolean;
};

type Tag = {
  label: string;
  value: string;
  isSuggested?: boolean;
  confidence?: number;
};

const INPUT_PROPS = { showMaxLengthLabel: false } as const;

const TagsField = ({ initialValues, setSelectedTags, disabled, placeholder, error }: TagsFieldProps) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<Tag[] | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { lorebookTagOptions: sortedTags } = useApp();
  const { displayToast } = useToast();

  const isTagsLimitReached = selectedOption && selectedOption.length >= MAXIMUM_TAG_LIMIT;

  const handleChange = useCallback(
    (newSelection: Tag[]) => {
      if (isTagsLimitReached && newSelection?.length === MAXIMUM_TAG_LIMIT) return displayToast({ message: "Tag limit reached" });

      const findTag = (tagObj: Tag) => tagObj.value === "Step-Family";

      const tagExist = (initialValues || []).find(findTag);

      const isTagInSelection = newSelection.some(findTag);

      if (tagExist && !isTagInSelection) {
        return setSelectedOption([...newSelection, tagExist]);
      }

      setSelectedOption(newSelection);
    },
    [isTagsLimitReached, initialValues, displayToast],
  );

  const handleMenuOpenChange = useCallback((value: boolean) => {
    setIsMenuOpen(value);

    // Fetch suggestions when menu opens
    if (value) {
      // fetchSuggestions();
    }
  }, []);

  useEffect(() => {
    setSelectedTags(selectedOption ?? []);
  }, [selectedOption]);

  // Update selectedOption when initialValues change
  useEffect(() => {
    if (initialValues && initialValues.length > 0 && !selectedOption) {
      setSelectedOption(initialValues);
    }
  }, [initialValues, selectedOption]);

  const allOptions = useMemo(() => {
    const uniqueTags = Array.from(new Set(sortedTags));

    const enabledTagOptions = uniqueTags.map((items: string) => ({
      label: items,
      value: items,
    }));

    return [{ heading: true, label: t("common:tags.all"), icon: IconVariants.SEARCH }, ...enabledTagOptions];
  }, [sortedTags, t]);

  // Convert Tag[] to string[] for MultiSelect
  const selectedValues = useMemo(() => (selectedOption || initialValues || []).map((tag: Tag) => tag.value), [selectedOption, initialValues]);

  const classNames = useMemo(() => ({ dropdownMenu: cn(isTagsLimitReached && "*:opacity-50") }), [isTagsLimitReached]);

  // Build a lookup map to avoid nested callbacks inside useCallback
  const existingTagMap = useMemo(() => {
    const map = new Map<string, Tag>();
    for (const tag of selectedOption || initialValues || []) {
      map.set(tag.value, tag);
    }
    return map;
  }, [selectedOption, initialValues]);

  // Handle MultiSelect onChange (receives string[])
  const handleMultiSelectChange = useCallback(
    (values: string[]) => {
      // Convert string[] back to Tag[] for internal handling
      const tagValues = values.map((tagValue: string) => existingTagMap.get(tagValue) || { label: tagValue, value: tagValue });
      handleChange(tagValues);
    },
    [existingTagMap, handleChange],
  );

  return (
    <MultiSelect
      disabled={disabled}
      options={allOptions}
      onChange={handleMultiSelectChange}
      selectedValues={selectedValues}
      closeMenuOnSelect={false}
      isMenuOpen={isMenuOpen}
      onOpenChange={handleMenuOpenChange}
      inputProps={INPUT_PROPS}
      maxSelectionLimit={MAXIMUM_TAG_LIMIT}
      classNames={classNames}
      placeholder={placeholder}
      error={error}
    />
  );
};

export default TagsField;
